<template>
  <div class="redeem-page page">

    <el-row :gutter="20">
      <el-col :span="14" :offset="6">
        <el-card v-if="!goodAccounts || !goodAccounts.length">
          <h1 class="title">Hold on, something doesn't look right...</h1>
          <p class="subtitle" style="text-align: left;">
            It looks like you haven't added an account yet. In order to get free Instagram likes, you need to add the account you want to get likes on to Likezoid. <br><br>
            We do this to prevent scammers and bots from abusing the app, plus we need to know which account to send likes to - sorry if it's causing any inconveniences!
          </p>
          <el-button type="primary" @click="navigate(Pages.Accounts)">Add an account now!</el-button>
        </el-card>

        <el-alert
          v-if="(!gold || gold <= 0) && goodAccounts && goodAccounts.length"
          title="Insufficient Gold balance"
          type="error"
          effect="dark"
          :closable="false">
          It looks like you don't have enough Gold to place an order. <br><br>
          You can earn more Gold by adding more accounts to Likezoid and waiting for them to start liking posts. Check out the Support page for more info!
        </el-alert>

        <el-card v-if="gold >= 1 && goodAccounts && goodAccounts.length" class="select-post">
          <div>
            <h1 class="title" style="text-align: center;">Let's get some free likes!</h1>
            <p class="subtitle">
              We just need a little bit of info so we know where to send the free likes to, as well as how many. Select your account, choose the amount of likes you want, select your post, and you're done!
            </p>
          </div>

          <AccountSelector @selectedAccount="selectedAccount" />

          <el-form>
            <el-form-item>
              <el-input-number
                class="w-100" placeholder="Number of likes"
                v-model="order.amount" :step="10" :min="10"
                :max="gold <= 500 ? gold : 500"></el-input-number>
            </el-form-item>

            <el-form-item v-if="!order.selectedPost">
              <el-button
                @click="openPostSelector"
                type="primary"
                :disabled="!targetAccount || !targetAccount.user_id || !order.amount || gold < order.amount"
                class="w-100">
                Select a post
              </el-button>
            </el-form-item>

            <el-form-item v-if="order.selectedPost">
              <el-button @click="clearSelectedPost" class="w-100">Choose a different post</el-button>
              <el-divider>or</el-divider>
              <el-button type="primary" @click="setDrawer(true)" class="w-100">Review &amp; Finish</el-button>
            </el-form-item>
          </el-form>
          
          <PostSelector v-if="targetAccount && targetAccount.user_id && showDialog" @close="close" @postSelected="postSelected"/>
        </el-card>
      </el-col>
    </el-row>

    <el-drawer v-if="selectedAccount && order.selectedPost" :visible.sync="drawer" :direction="direction">
      <div>
        <div class="checkout-header">
          <el-row class="line-item">
            <el-col :span="22" :offset="1">
              <h1 class="title">Almost done! 👍</h1>
              <p class="subtitle">Review your order for your free Instagram likes, then checkout :)</p>
            </el-col>
          </el-row>
        </div>

        <el-divider></el-divider>
        <el-row class="line-item">
          <el-col :span="4" :offset="1">
            <el-avatar :src="order.selectedPost.node.thumbnail_src" style="width: 60px; height: 60px;"  fit="fill" shape="square"></el-avatar>
          </el-col>
          <el-col :span="18" class="item-description">
            {{ orderAmount }} free likes <br/>
            <span class="text-info">(You'll end up with {{ order.selectedPost.node.edge_media_preview_like.count + orderAmount}} likes total)</span>
          </el-col>
        </el-row>

        <el-divider></el-divider>

        <el-row class="line-item">
          <el-col :span="22" :offset="1" class="item-total">
            This order will cost you <span class="gold-spent">{{ orderAmount }} Gold</span>. Orders are non-refundable, so please make sure your account is set to <strong>public</strong>, and don't delete the post.
          </el-col>
        </el-row>

        <el-row class="line-item" style="margin-top: 20px;">
          <el-col :span="22" :offset="1" class="item-total">
            <el-button :loading="isCheckingOut" @click="checkout" type="primary" style="width:100%; height: 50px;">Checkout</el-button>
            <p class="text-info">By clicking submitting this form you agree to Likezoid's Terms &amp; Conditions located on Likezoid.com.</p>
          </el-col>
        </el-row>
        
      </div>  
    </el-drawer>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { InstagramAPI } from '../bridge';
import AccountSelector from '@/components/AccountSelector'
import PostSelector from '@/components/PostSelector'

export default {
  name: 'redeem',
  components: {
    AccountSelector,
    PostSelector,
  },
  data () {
    return {
      drawer: false,
      direction: 'rtl',
      showDialog: false,
      user: {},
      packages: [
        { id: 1, amount: 50, price: 50 },
        { id: 2, amount: 100, price: 100 },
        { id: 3, amount: 250, price: 250 },
        { id: 4, amount: 500, price: 500 },
      ],

      isCheckingOut: false,
      order: {},
    };
  },
  computed: {
    ...mapGetters([
      'gold',
      'allAccounts',
      'targetAccount',
      'currentPage',
      'Pages'
    ]),
    goodAccounts () {
      return this.allAccounts.filter(a => !a.needsLogin || a.likeBlocked);
    },
    orderAmount () {
      return this.order.amount;
    }
  },
  methods: {
    ...mapActions({
      setGold: 'setGold',
      setTargetAccount: 'setTargetAccount',
      createOrder: 'createOrder',
      navigate: 'changePage'
    }),
    setDrawer (value) {
      this.drawer = value;
      this.$forceUpdate();
    },
    downloadBase64Image(url) {
      const str = InstagramAPI.remoteImageToBase64(url).then(t => t);
      return str;
    },
    checkout () {
      try {
        this.isCheckingOut = true;
        let order = {
          target: this.order.selectedPost.node.shortcode,
          amount: this.order.amount,
          before: this.order.selectedPost.node.edge_media_preview_like.count,
          user_id: this.order.selectedPost.node.owner.id,
          post_id: this.order.selectedPost.node.id,
        };
        
        console.log(order); // eslint-disable-line

        if (order.target.length > 16) {
          this.$notify.error({
            title: 'Error submitting order',
            message: `It looks like the post is private - we got the url: https://instagram.com/p/${order.target}/. Please make sure your profile is set to "public", restart Likezoid, and try again. If this issue persists, please contact support with a screenshot of this message.`,
            position: 'bottom-right',
          });
          this.isCheckingOut = false;
          return;
        }

        this.createOrder(order).then(() => {
          this.$notify.success({
            title: 'Success!',
            message: `Your order was submitted successfully! You should start recieving likes soon.`,
            duration: 10000,
            offset: 100,
          });
          this.isCheckingOut = false;
          this.clearSelectedPost();
          this.order = {};
          this.drawer = false;
          this.$forceUpdate();
        }).catch(err => {
          let msg = `It looks like an error occurred while we were adding your post`;
          if (typeof(err) === 'string') {
            msg = err;
          }
          this.$notify.error({
            title: 'Error submitting order',
            message: msg,
            duration: 10000,
            position: 'bottom-right'
          });
          this.isCheckingOut = false;
        });
      } catch (error) {
        console.log(error); // eslint-disable-line
        console.error(error); // eslint-disable-line
        this.isCheckingOut = false;
        this.$notify.error({
          title: 'Error adding order',
          message: `Error message: ${error.message || 'An unknown error occurred'}`,
          duration: 0,
          position: 'bottom-right'
        });
      }
    },
    clearSelectedPost () {
      this.order.selectedPost = false;
      this.$forceUpdate();
    },
    postSelected (post) {
      this.order.selectedPost = post;
    },
    openPostSelector () {
      this.showDialog = true;
    },
    selectedAccount (user) {
      this.setTargetAccount(user);
      this.clearSelectedPost();
    },
    close (params) {
      if (params.error) {
        this.$notify.error({
          title: 'Error',
          message: params.message,
          position: 'bottom-right'
        });
      }
      this.showDialog = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.redeem-page {
  height: 100%;

  > * {
    // position: relative;
    z-index: 200;
  }

  &:after {
    content: '';
    position: absolute;
    margin-left: -349px;
    left: 50%;
    bottom: 0;
    width: 698px;
    height: 346px;
    background-image: url('../assets/img/view/redeem.svg');
    background-size: 100% 100%;
    background-position: center;
    background-repeat: no-repeat;
    z-index: 0;
  }
}

.line-item {
  font-size: 18px;

  .item-description,
  .item-price {
    padding-top: 12px;
    padding-left: 12px;
    .text-info {
      color: #909399;
      font-size: 10px;
    }
  }
  .item-total {
    padding-top: 18px;
    text-align: center;
    font-size: 16px;
    .text-info {
      color: #909399;
      font-size: 10px;
    }
  }
}

.el-card.select-post {
  margin-top: 39px;

  .title {
    white-space: nowrap;
  }
}

::v-deep .el-drawer__close-btn {
  -webkit-app-region: no-drag;
}

::v-deep .el-drawer__header {
  margin-bottom: 0px !important;
}

.checkout-header {
  text-align: center;
}
</style>